import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { apiService, DestinoPorEspera, HitRateResponse, HitRateSummaryRow } from '../services/api';
import ReplenishmentOrders, { OrderDetailModal } from '../components/ReplenishmentOrders';
import {
  ALL_STORES, DESTINOS, ONLINE_STORE, PHYSICAL_STORES, TOTAL, fmtDay, fmtEur, fmtHours, fmtInt, fmtPct, storeLabel,
} from '../utils/replenishment';

const PRESETS = [7, 28, 90];
const DEFAULT_PRESET = 28;

const STORE_COLORS: Record<string, string> = {
  A000: '#1f77b4', B000: '#ff7f0e', C000: '#2ca02c',
  D000: '#9467bd', E000: '#8c564b', F000: '#17becf', [ONLINE_STORE]: '#d62728', [TOTAL]: '#212529',
};

// Datas de calendário em Lisboa (YYYY-MM-DD) — evita desvios de fuso do toISOString().
const lisbonToday = (): string =>
  new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Lisbon' });

const shiftDate = (isoDate: string, days: number): string => {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const fmtTimestamp = (ts: string | null): string => {
  if (!ts) return '-';
  return new Date(ts).toLocaleString('pt-PT', {
    timeZone: 'Europe/Lisbon', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const Kpi: React.FC<{ title: string; value: string; hint?: string }> = ({ title, value, hint }) => (
  <div className="col-6 col-lg">
    <div className="card h-100">
      <div className="card-body">
        <div className="text-muted small">{title}</div>
        <div className="fs-3 fw-semibold">{value}</div>
        {hint && <div className="text-muted small">{hint}</div>}
      </div>
    </div>
  </div>
);

// Destino final das encomendas que tiveram / não tiveram espera ('Aguardar produto')
const DestinoPorEsperaTable: React.FC<{ rows: DestinoPorEspera[] }> = ({ rows }) => {
  const grupos: Array<['LOJAS' | 'T000', string]> = [['LOJAS', 'Lojas A–F'], ['T000', 'Online T000']];
  const destinos = Object.keys(DESTINOS).filter((d) => d !== 'cancelada');
  const share = (grupo: string, espera: boolean, destino: string) => {
    const sel = rows.filter((r) => r.grupo === grupo && r.teve_espera === espera);
    const total = sel.reduce((a, r) => a + r.n, 0);
    const n = sel.filter((r) => r.destino === destino).reduce((a, r) => a + r.n, 0);
    return total ? fmtPct((100 * n) / total) : '-';
  };
  const count = (grupo: string, espera: boolean) =>
    rows.filter((r) => r.grupo === grupo && r.teve_espera === espera).reduce((a, r) => a + r.n, 0);
  return (
    <div className="table-responsive">
      <div className="small text-muted mb-1">Destino conforme a encomenda teve ou não espera (encomendas totalmente canceladas não entram)</div>
      <table className="table table-sm mb-0 w-auto">
        <thead className="table-light">
          <tr>
            <th>Grupo</th><th>Teve espera?</th><th className="text-end">Encomendas</th>
            {destinos.map((d) => <th key={d} className="text-end">{DESTINOS[d].label}</th>)}
          </tr>
        </thead>
        <tbody>
          {grupos.flatMap(([g, label]) => [false, true].map((espera) => (
            <tr key={`${g}-${espera}`}>
              <td>{label}</td>
              <td>{espera ? 'Sim' : 'Não'}</td>
              <td className="text-end">{fmtInt(count(g, espera))}</td>
              {destinos.map((d) => <td key={d} className="text-end">{share(g, espera, d)}</td>)}
            </tr>
          )))}
        </tbody>
      </table>
    </div>
  );
};

const ReplenishmentPage: React.FC = () => {
  const today = useMemo(lisbonToday, []);
  const [params, setParams] = useSearchParams();

  // Estado partilhável no URL: start, end, stores, store (drill down), doc (encomenda)
  const start = ISO_DATE.test(params.get('start') || '') ? params.get('start')! : shiftDate(today, -(DEFAULT_PRESET - 1));
  const end = ISO_DATE.test(params.get('end') || '') ? params.get('end')! : today;
  const stores = useMemo(() => {
    const raw = (params.get('stores') || '').split(',').filter((c) => ALL_STORES.includes(c));
    return raw.length ? raw : ALL_STORES;
  }, [params]);
  const drillStore = params.get('store');
  const docParam = params.get('doc');

  const patchParams = useCallback((changes: Record<string, string>, push = false) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      return next;
    }, { replace: !push });
  }, [setParams]);

  const setStart = (v: string) => patchParams({ start: v, page: '' });
  const setEnd = (v: string) => patchParams({ end: v, page: '' });
  const setStores = (list: string[]) =>
    patchParams({ stores: list.length === ALL_STORES.length ? '' : list.join(',') });

  const [showEstimated, setShowEstimated] = useState(false);
  const [data, setData] = useState<HitRateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!start || !end || start > end || stores.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      setData(await apiService.getStoreHitRate({
        start, end, stores: stores.length === ALL_STORES.length ? undefined : stores.join(','),
      }));
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [start, end, stores]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (days: number) =>
    patchParams({ start: shiftDate(today, -(days - 1)), end: today, page: '' });

  const toggleStore = (code: string) =>
    setStores(stores.includes(code) ? stores.filter((s) => s !== code) : [...stores, code].sort());

  const activePreset = end === today ? PRESETS.find((d) => start === shiftDate(today, -(d - 1))) : undefined;

  // Pivot: uma linha por dia, colunas por loja (hit rate e encomendas). Dias sem
  // encomendas ficam a null (a linha parte em vez de cair para 0%).
  const chartData = useMemo(() => {
    if (!data) return [];
    const byDay = new Map<string, Record<string, any>>();
    for (const r of data.daily) {
      const row = byDay.get(r.date) || { date: r.date };
      row[`hr_${r.loja}`] = r.encomendas > 0 ? r.hit_rate : null;
      row[`hrr_${r.loja}`] = r.encomendas_sap > 0 ? r.hit_rate_real : null;
      row[`w_${r.loja}`] = r.espera_mediana_horas ?? null;
      row[`n_${r.loja}`] = r.encomendas;
      byDay.set(r.date, row);
    }
    return Array.from(byDay.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  const total: HitRateSummaryRow | undefined = data?.summary.find((s) => s.loja === TOTAL);
  const online: HitRateSummaryRow | undefined = data?.summary.find((s) => s.loja === ONLINE_STORE);
  const storeRows = data?.summary.filter((s) => s.loja !== TOTAL && s.loja !== ONLINE_STORE) ?? [];
  const seriesStores = data?.stores ?? [];
  // Barras de encomendas: o T000 (~700/dia) esmagaria a escala das lojas
  const barStores = seriesStores.length === 1 ? seriesStores : seriesStores.filter((c) => c !== ONLINE_STORE);

  // Lojas físicas a listar no drill down do TOTAL
  const physicalSelected = stores.filter((c) => c !== ONLINE_STORE);
  const openStore = (code: string) => patchParams({ store: code, doc: '', result: '', causa: '', q: '', page: '', order: '', pdays: '' }, true);
  const drillStores = drillStore === TOTAL ? physicalSelected : drillStore ? [drillStore] : [];
  const drillValid = drillStore && (drillStore === TOTAL || ALL_STORES.includes(drillStore)) && drillStores.length > 0;

  return (
    <div className="container-fluid py-3">
      <h2 className="mb-1">🏬 Replenishment — Hit Rate das Lojas</h2>
      <p className="text-muted small">
        Encomendas online com levantamento em loja em que a loja tinha stock de todos os artigos
        no momento da encomenda.
      </p>

      {/* Filtros */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-auto">
              <label className="form-label small mb-1">Início</label>
              <input type="date" className="form-control form-control-sm" value={start} max={end}
                     onChange={(e) => setStart(e.target.value)} />
            </div>
            <div className="col-auto">
              <label className="form-label small mb-1">Fim</label>
              <input type="date" className="form-control form-control-sm" value={end} min={start} max={today}
                     onChange={(e) => setEnd(e.target.value)} />
            </div>
            <div className="col-auto">
              <div className="btn-group btn-group-sm" role="group">
                {PRESETS.map((d) => (
                  <button key={d} type="button"
                          className={`btn ${activePreset === d ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => applyPreset(d)}>
                    {d} dias
                  </button>
                ))}
              </div>
            </div>
            <div className="col-12 col-lg">
              <label className="form-label small mb-1 d-block">Lojas</label>
              <div className="d-flex flex-wrap gap-1 align-items-center">
                <button type="button"
                        className={`btn btn-sm ${stores.length === ALL_STORES.length ? 'btn-secondary' : 'btn-outline-secondary'}`}
                        onClick={() => setStores(ALL_STORES)}>
                  Todas
                </button>
                <button type="button"
                        className={`btn btn-sm ${stores.join() === PHYSICAL_STORES.join() ? 'btn-secondary' : 'btn-outline-secondary'}`}
                        onClick={() => setStores(PHYSICAL_STORES)}>
                  Só lojas
                </button>
                {ALL_STORES.map((code) => (
                  <button key={code} type="button"
                          className={`btn btn-sm ${stores.includes(code) ? 'btn-primary' : 'btn-outline-primary'}`}
                          onClick={() => toggleStore(code)}>
                    {storeLabel(code)}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {start > end && <div className="text-danger small mt-2">A data de início tem de ser anterior ao fim.</div>}
          {stores.length === 0 && <div className="text-danger small mt-2">Seleccione pelo menos uma loja.</div>}
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && <div className="text-muted mb-2">A carregar...</div>}

      {data && !error && (!total && !online ? (
        <div className="alert alert-info">Sem encomendas no período seleccionado.</div>
      ) : (
        <>
          {data.sap_history_start && (
            <div className="text-muted small mb-2">
              Hit real (SAP) disponível desde {data.sap_history_start.split('-').reverse().join('/')}
              {data.start < data.sap_history_start
                ? ' — o hit real, as esperas e a concordância só usam encomendas a partir dessa data.'
                : '.'}
            </div>
          )}

          {/* KPIs do período: lojas A–F (hit real SAP como principal) + cartão do online (T000) */}
          <div className="row g-3 mb-3">
            {total && (
              <>
                <Kpi title="Hit rate real (SAP)" value={fmtPct(total.hit_rate_real)}
                     hint={`${fmtInt(total.hits_sap)} de ${fmtInt(total.encomendas_sap)} encomendas sem 'Aguardar produto'`} />
                <Kpi title="Hit rate estimado (stock)" value={fmtPct(total.hit_rate)}
                     hint={total.concordancia != null
                       ? `Concordância com o real: ${fmtPct(total.concordancia)}`
                       : `${fmtInt(total.hits)} de ${fmtInt(total.encomendas)} encomendas`} />
                <Kpi title="Encomendas lojas" value={fmtInt(total.encomendas)} />
                <Kpi title="Hit rate estimado em valor" value={fmtPct(total.hit_rate_valor)}
                     hint={`${fmtEur(total.net_hit)} de ${fmtEur(total.net_total)}`} />
                <Kpi title="Falhas com stock no T000" value={fmtPct(total.pct_falhas_com_stock_t)}
                     hint={`Hit rate potencial: ${fmtPct(total.hit_rate_potencial)}`} />
              </>
            )}
          </div>
          <div className="row g-3 mb-3">
            {total && (
              <>
                <Kpi title="Espera mediana" value={fmtHours(total.espera_mediana_horas)}
                     hint={`${fmtInt(total.encomendas_com_espera)} encomendas com espera`} />
                <Kpi title="Espera média" value={fmtHours(total.espera_media_horas)} />
                <Kpi title="Resolvidas em ≤ 24 h" value={fmtPct(total.pct_espera_ate_24h)}
                     hint={`≤ 48 h: ${fmtPct(total.pct_espera_ate_48h)}`} />
                <Kpi title="Encomendas pendentes" value={fmtInt(total.pendentes)}
                     hint="Ainda sem estar prontas p/ levantamento" />
              </>
            )}
            {online && (
              <Kpi title="Online T000 — hit real" value={fmtPct(online.hit_rate_real)}
                   hint={`Estimado ${fmtPct(online.hit_rate)} · ${fmtInt(online.encomendas)} encomendas`
                     + (online.encomendas_excluidas ? ` · ${fmtInt(online.encomendas_excluidas)} excluídas` : '')
                     + ` · espera até envio mediana ${fmtHours(online.espera_mediana_horas)}`} />
            )}
          </div>

          {/* Gráficos */}
          <div className="row g-3 mb-3">
            <div className="col-12 col-xl-7">
              <div className="card h-100">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <span>
                    Hit rate real (SAP) diário (%)
                    {data.sap_history_start && (
                      <span className="text-muted small ms-2">
                        disponível desde {data.sap_history_start.split('-').reverse().join('/')}
                      </span>
                    )}
                  </span>
                  <div className="form-check form-switch mb-0 small">
                    <input className="form-check-input" type="checkbox" id="showEstimated" checked={showEstimated}
                           onChange={(e) => setShowEstimated(e.target.checked)} />
                    <label className="form-check-label" htmlFor="showEstimated">Mostrar estimado (tracejado)</label>
                  </div>
                </div>
                <div className="card-body" style={{ height: 340 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={fmtDay} />
                      <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                      <Tooltip labelFormatter={(l) => fmtDay(String(l))}
                               formatter={(v: any, name: any) => [fmtPct(v as number), name]} />
                      <Legend />
                      {seriesStores.map((code) => (
                        <Line key={code} type="monotone" dataKey={`hrr_${code}`} name={storeLabel(code)}
                              stroke={STORE_COLORS[code]} strokeWidth={1.5} dot={false} connectNulls />
                      ))}
                      {total && seriesStores.filter((c) => c !== ONLINE_STORE).length > 1 && (
                        <Line type="monotone" dataKey={`hrr_${TOTAL}`} name="Total lojas"
                              stroke={STORE_COLORS[TOTAL]} strokeWidth={3} dot={false} connectNulls />
                      )}
                      {showEstimated && [...seriesStores, ...(total ? [TOTAL] : [])].map((code) => (
                        <Line key={`est_${code}`} type="monotone" dataKey={`hr_${code}`}
                              name={`${storeLabel(code)} (estimado)`} stroke={STORE_COLORS[code]}
                              strokeWidth={1} strokeDasharray="4 3" dot={false} connectNulls legendType="none" />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            <div className="col-12 col-xl-5">
              <div className="card h-100">
                <div className="card-header">
                  Encomendas por dia{barStores.length === 1 && barStores[0] === ONLINE_STORE ? '' : ' (lojas)'}
                </div>
                <div className="card-body" style={{ height: 340 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={fmtDay} />
                      <YAxis allowDecimals={false} />
                      <Tooltip labelFormatter={(l) => fmtDay(String(l))} />
                      <Legend />
                      {barStores.map((code) => (
                        <Bar key={code} dataKey={`n_${code}`} name={storeLabel(code)}
                             stackId="orders" fill={STORE_COLORS[code]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-12 col-xl-7">
              <div className="card h-100">
                <div className="card-header">
                  Espera mediana diária (horas até ficar pronta)
                  <span className="text-muted small ms-2">encomendas que passaram por 'Aguardar produto'; T000 = até envio</span>
                </div>
                <div className="card-body" style={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tickFormatter={fmtDay} />
                      <YAxis tickFormatter={(v) => `${v} h`} />
                      <Tooltip labelFormatter={(l) => fmtDay(String(l))}
                               formatter={(v: any, name: any) => [fmtHours(v as number), name]} />
                      <Legend />
                      {seriesStores.filter((c) => c !== ONLINE_STORE).map((code) => (
                        <Line key={code} type="monotone" dataKey={`w_${code}`} name={storeLabel(code)}
                              stroke={STORE_COLORS[code]} strokeWidth={1.5} dot={false} connectNulls />
                      ))}
                      {total && seriesStores.filter((c) => c !== ONLINE_STORE).length > 1 && (
                        <Line type="monotone" dataKey={`w_${TOTAL}`} name="Total lojas"
                              stroke={STORE_COLORS[TOTAL]} strokeWidth={3} dot={false} connectNulls />
                      )}
                      {seriesStores.includes(ONLINE_STORE) && (
                        <Line type="monotone" dataKey={`w_${ONLINE_STORE}`} name={storeLabel(ONLINE_STORE)}
                              stroke={STORE_COLORS[ONLINE_STORE]} strokeWidth={1.5} dot={false} connectNulls
                              strokeDasharray="5 3" />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Destino final das encomendas */}
          <div className="card mb-3">
            <div className="card-header">
              Destino das encomendas
              <span className="text-muted small ms-2">
                estado actual no SAP · encomendas com menos de 7 dias ainda em curso
              </span>
            </div>
            <div className="card-body">
              <div className="row g-3 mb-3">
                {total && (
                  <>
                    <Kpi title="Entregues" value={fmtPct(total.pct_entregue)} hint={fmtEur(total.net_entregues)} />
                    <Kpi title="Canceladas" value={fmtPct(total.pct_cancelada)} hint={fmtEur(total.net_canceladas)} />
                    <Kpi title="Prontas, não levantadas" value={fmtPct(total.pct_nao_levantada)}
                         hint={fmtEur(total.net_nao_levantadas)} />
                    <Kpi title="Abertas" value={fmtPct(total.pct_aberta)} hint={fmtEur(total.net_abertas)} />
                    <Kpi title="Valor perdido" value={fmtEur(total.valor_perdido)}
                         hint="cancelada + não levantada + aberta (lojas A–F)" />
                  </>
                )}
              </div>
              {data.destino_x_espera && data.destino_x_espera.length > 0 && (
                <DestinoPorEsperaTable rows={data.destino_x_espera} />
              )}
            </div>
          </div>

          {/* Tabela agregada do período — linhas clicáveis (drill down) */}
          <div className="card mb-3">
            <div className="card-header">
              Período {fmtDay(data.start)} → {fmtDay(data.end)} por loja
              <span className="text-muted small ms-2">clique numa linha para ver as encomendas</span>
            </div>
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Loja</th>
                    <th className="text-end">Encomendas</th>
                    <th className="text-end">Hit real</th>
                    <th className="text-end">Hit estimado</th>
                    <th className="text-end">Espera mediana</th>
                    <th className="text-end">Espera média</th>
                    <th className="text-end">≤ 24 h</th>
                    <th className="text-end">≤ 48 h</th>
                    <th className="text-end">Pendentes</th>
                    <th className="text-end">% entregue</th>
                    <th className="text-end">% cancelada</th>
                    <th className="text-end">% não levantada</th>
                    <th className="text-end">% aberta</th>
                    <th className="text-end">Valor perdido</th>
                    <th className="text-end">Hit 1 SKU</th>
                    <th className="text-end">Hit multi-SKU</th>
                    <th className="text-end">Hit rate valor</th>
                    <th className="text-end">Falhas c/ stock T</th>
                    <th className="text-end">Hit potencial</th>
                    <th className="text-end">Valor líquido</th>
                  </tr>
                </thead>
                <tbody>
                  {[...storeRows, ...(total ? [total] : []), ...(online ? [online] : [])].map((r) => (
                    <tr key={r.loja}
                        style={{ cursor: 'pointer' }}
                        className={[
                          r.loja === TOTAL ? 'fw-bold table-light' : '',
                          r.loja === ONLINE_STORE ? 'border-top border-2' : '',
                          drillStore === r.loja ? 'table-active' : '',
                        ].join(' ')}
                        onClick={() => openStore(r.loja)}>
                      <td>{storeLabel(r.loja)}</td>
                      <td className="text-end">
                        {fmtInt(r.encomendas)}
                        {r.encomendas_excluidas > 0 && (
                          <span className="text-muted small ms-1" title="Encomendas excluídas do hit rate (N000 fornecedor / R000 recondicionados)">
                            (+{fmtInt(r.encomendas_excluidas)} excl.)
                          </span>
                        )}
                      </td>
                      <td className="text-end">{fmtPct(r.hit_rate_real)}</td>
                      <td className="text-end">{fmtPct(r.hit_rate)}</td>
                      <td className="text-end">{fmtHours(r.espera_mediana_horas)}</td>
                      <td className="text-end">{fmtHours(r.espera_media_horas)}</td>
                      <td className="text-end">{fmtPct(r.pct_espera_ate_24h)}</td>
                      <td className="text-end">{fmtPct(r.pct_espera_ate_48h)}</td>
                      <td className="text-end">{fmtInt(r.pendentes)}</td>
                      <td className="text-end">{fmtPct(r.pct_entregue)}</td>
                      <td className="text-end">{fmtPct(r.pct_cancelada)}</td>
                      <td className="text-end">{fmtPct(r.pct_nao_levantada)}</td>
                      <td className="text-end">{fmtPct(r.pct_aberta)}</td>
                      <td className="text-end">{fmtEur(r.valor_perdido)}</td>
                      <td className="text-end" title={`${fmtInt(r.encomendas_1sku)} encomendas`}>{fmtPct(r.hit_rate_1sku)}</td>
                      <td className="text-end" title={`${fmtInt(r.encomendas_multi)} encomendas`}>{fmtPct(r.hit_rate_multi)}</td>
                      <td className="text-end">{fmtPct(r.hit_rate_valor)}</td>
                      <td className="text-end" title={`${fmtInt(r.falhas_com_stock_t)} encomendas`}>{fmtPct(r.pct_falhas_com_stock_t)}</td>
                      <td className="text-end">{fmtPct(r.hit_rate_potencial)}</td>
                      <td className="text-end">{fmtEur(r.net_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {drillValid && (
            <ReplenishmentOrders start={data.start} end={data.end} stores={drillStores}
                                 scopeCode={drillStore!} />
          )}
          {docParam && !drillValid && (
            <OrderDetailModal doc={docParam} onClose={() => patchParams({ doc: '' })} />
          )}

          <p className="text-muted small">
            <strong>Método:</strong> hit = no último snapshot de stock anterior à hora da encomenda, a loja
            tinha quantidade disponível ≥ encomendada para todos os artigos. Os snapshots são ~3 por dia
            (07:30, 17:30, 23:59), pelo que o stock usado pode ter até ~10h de atraso face à encomenda.
            Hit potencial = hit se o artigo estivesse na loja ou no T000. O Total soma só as lojas A–F;
            o T000 (online com envio) é medido sobre o stock do próprio T000 e exclui encomendas com linhas
            N000 (fornecedor) ou R000 (recondicionados). Encomendas canceladas excluídas;
            os últimos 14 dias são recalculados diariamente (cancelamentos tardios e esperas por resolver).
            {' '}<strong>Hit real (SAP):</strong> nenhuma linha do site passou por "Aguardar Produto" no histórico
            de estados do SAP (hora da carga, precisão ~1 h). Espera = da encomenda até todas as linhas
            do site ficarem prontas p/ levantamento; só entra quem passou por "Aguardar Produto". A mediana é
            calculada sobre as encomendas do período. Encomendas anteriores ao início do histórico (22/07)
            ou sem histórico completo ficam fora do hit real. No T000 a espera é até ao envio.
            {' '}Última actualização: {fmtTimestamp(data.calculated_at)}.
          </p>
        </>
      ))}
    </div>
  );
};

export default ReplenishmentPage;
